/*
 * Copyright (c) Nexign, JSC, 2019-2025.
 */
/**
 * Table component for Bills
 *
 * @author Yakov Gushchin
 * @author Eugenij Tyan
 * @author Aleksandr Perkhunov
 * @author Anton Panasenko
 * @author Evgenij Tyan
 * @author Evgeniy Naumov
 * @author Nikolay Sviridov
 * @version 1.36.0
 * @since 1.0.0
 */
import { getIsFastActionPage } from '@/main/components/Table/utils/tableUtils';
import { EOnboardingComponentsId } from '@/main/modules/onboarding/types/onboardingTypes';

import { Table } from '../../../../components/common';
import { IBasketItem } from '../../../../components/Table/components/footerModal/Basket/basketTypes';
import { ITableField, ITableReducer } from '../../../../components/Table/reducers/tableReducer';
import TableColumnUtil from '../../../../components/Table/TableColumnUtil';
import {
    IFieldResize,
    ISetTableSortPayload,
    ISwitchCheckboxPayload,
    TableNameType,
} from '../../../../components/Table/types/tableTypes';
import { initTranslation } from '../../../../modules/internationalization/constants';
import {
    ICommonTranslation,
    ICorporateTranslation,
    IPageBillsTranslation,
} from '../../../../modules/internationalization/types';
import { guardComponentType } from '../../../../modules/role/reducer/guardReducer';
import { billsColumnNames, BillsField, PageBillsType } from '../../pageBillsTypes';
import { cellConfig, getColumnCellType, getColumnFilterType, getColumnFixedType } from './Columns/Cells';
import BillsTableFooter from './Footer/BillsTableFooter';

type BillsTranslationType = ICommonTranslation & ICorporateTranslation & IPageBillsTranslation;

export interface IBillsTableProps {
    basketAllIn: boolean;
    billsType: PageBillsType;
    componentsFeature: guardComponentType;
    counter: number;
    clientId: string;
    data: ITableReducer[TableNameType]['data'];
    disableCheckedAll: boolean;
    fields: Array<ITableField<BillsField>>;
    isDensity: boolean;
    isLoading: boolean;
    isTableResizeEnabled: boolean;
    loadTableList: () => void;
    saveResizedChange: (tableFields: IFieldResize[]) => void;
    setTableSort: (payload: ISetTableSortPayload) => void;
    sort: ITableReducer[TableNameType]['sort'];
    switchCheckbox: (payload: ISwitchCheckboxPayload) => void;
    tableCheckedItems: IBasketItem[];
    translation: BillsTranslationType;
}

/**
 * @description Get table column util exemplar
 * @return {TableColumnUtil}
 */
const tableColumnUtil = (props: IBillsTableProps) =>
    new TableColumnUtil<BillsField, IPageBillsTranslation & ICorporateTranslation & ICommonTranslation>({
        cellConfig,
        componentsFeature: props.componentsFeature,
        fields: props.fields,
        isTableResizeEnabled: props.isTableResizeEnabled,
        getColumnCellType,
        getColumnFilterType,
        getColumnFixedType,
        columnNames: billsColumnNames,
        translation: props.translation,
    });

const BillsTable = (props: IBillsTableProps) => {
    const isFastActionsPage = getIsFastActionPage();
    return (
        <Table
            basketAllIn={props.basketAllIn}
            columns={tableColumnUtil(props).customizeTableColumns()}
            counter={props.counter}
            data={props.data}
            disableCheckedAll={props.disableCheckedAll}
            disableCheckedAllMessage={props.translation.table.disableCheckedAllMessage}
            disableSortCheckbox={true}
            fields={props.fields}
            footer={!isFastActionsPage && <BillsTableFooter billsType={props.billsType} clientId={props.clientId} />}
            getProps={() => ({ className: 'table-with-checkboxes' })}
            isDensity={props.isDensity}
            isLoading={props.isLoading}
            isTableFooter={true}
            loadTableList={props.loadTableList}
            onboarding={EOnboardingComponentsId.billsTable}
            rowDataName="number"
            saveResizedChange={props.saveResizedChange}
            setTableSort={props.setTableSort}
            showCheckedColumn={true}
            sort={props.sort}
            switchCheckbox={props.switchCheckbox}
            tableCheckedItems={props.tableCheckedItems}
            tableDataName="bills"
            tableName={TableNameType.bills}
        />
    );
}

BillsTable.defaultProps = {
    translation: {
        ...initTranslation.common,
        ...initTranslation.corporate,
        ...initTranslation.pageBills,
    },
};

export default BillsTable;
